import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import bwipjs from '@bwip-js/browser';
import { DomSanitizer } from '@angular/platform-browser';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, concat, filter, interval, map, Observable, of, switchMap } from 'rxjs';

@Component({
  selector: 'ticketing-qr-code',
  imports: [],
  templateUrl: './qr-code.component.html',
  styleUrl: './qr-code.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QrCodeComponent {
  private sanitizer = inject(DomSanitizer);

  readonly payload = input.required<string>();

  readonly withTimeStamp = input<boolean>(false);

  svg = computed(() => {
    const container = this.codeContainer();
    if (container) {
      return this.sanitizer.bypassSecurityTrustHtml(container);
    }
    return undefined;
  });

  public codeContainer = toSignal(
    combineLatest([toObservable(this.payload), this.qrCodeGeneratorTimeStampGenerator()]).pipe(
      filter(([payload]) => !!payload),
      map(([payload, now]) => [payload, now] as [string, Date]),
      map(
        ([payload, now]) =>
          bwipjs
            .toSVG({
              bcid: 'azteccode',
              text: this.withTimeStamp() ? payload + ';' + now.toISOString() : payload,
            })
            .replace(/fill="[^"]*"/g, 'fill="inherit"'), //remove default black fill so the code fills itself with the color of the parent in the template later
      ),
    ),
  );

  private areSecondsModuloTenEqualToNull(timestamp: Date): boolean {
    return timestamp.getSeconds() % 10 === 0;
  }

  private qrCodeGeneratorTimeStampGenerator(): Observable<Date> {
    return of(new Date()).pipe(
      switchMap(timeStamp => {
        timeStamp.setSeconds(timeStamp.getSeconds() - (timeStamp.getSeconds() % 10));

        const intervalEmit$ = interval(1000).pipe(
          map(() => new Date()),
          filter(this.areSecondsModuloTenEqualToNull),
        );

        return concat(of(timeStamp), intervalEmit$);
      }),
    );
  }
}
